/**
 * Per-run consent for capabilities that act with the signed-in viewer's
 * authority.
 *
 * A broker existing in the React tree is deliberately not enough. A caller
 * must create a consent context from an explicit user action and attach it to
 * that one run. Automated callers omit `runContext.consent`, so the runner
 * refuses without opening a dialog.
 */

import { mapRunContext } from "./kernelResolver.js";
import { createPythonWheelBroker } from "./pythonWheelBroker.js";

export const RUN_CONSENT_REFUSAL = Object.freeze({
    NO_ACTIVE_USER: "consent_user_not_present",
    NO_BROKER: "consent_broker_unavailable",
    MISSING_TRIGGER: "consent_trigger_missing",
    INVALID_REQUEST: "consent_request_invalid",
    DECLINED: "consent_declined",
    BROKER_FAILED: "consent_broker_failed",
    CANCELLED: "consent_run_cancelled",
});

const REFUSAL_REASONS = new Set(Object.values(RUN_CONSENT_REFUSAL));
const BROKER_ABORTED = Symbol("run-consent-broker-aborted");

const REFUSAL_COPY = Object.freeze({
    [RUN_CONSENT_REFUSAL.NO_ACTIVE_USER]: "This action needs a user to approve it during this run.",
    [RUN_CONSENT_REFUSAL.NO_BROKER]: "This surface cannot ask for approval, so the action was refused.",
    [RUN_CONSENT_REFUSAL.MISSING_TRIGGER]: "The run did not preserve what asked for this action, so it was refused.",
    [RUN_CONSENT_REFUSAL.INVALID_REQUEST]: "The action could not be described safely, so it was refused.",
    [RUN_CONSENT_REFUSAL.DECLINED]: "The user declined this action.",
    [RUN_CONSENT_REFUSAL.BROKER_FAILED]: "Approval could not be completed, so the action was refused.",
    [RUN_CONSENT_REFUSAL.CANCELLED]: "The run ended before approval was completed.",
});

/**
 * Create the consent portion of a run context from an explicit user action.
 *
 * `triggerText` is the user's turn when the capability traces to one.
 * `modelReason` is the model's stated reason when it does not. The user text
 * wins when both are supplied.
 */
export function createInteractiveConsentContext({
    broker,
    triggerText,
    modelReason,
} = {}) {
    const trigger = normalizeTrigger({ triggerText, modelReason });
    return Object.freeze({
        userPresent: true,
        broker: broker || null,
        trigger,
    });
}

/**
 * Convenience helper for UI call sites that already own the rest of the run
 * context (definition/invocation source, scope, attribution, etc.).
 */
export function withInteractiveConsent(runContext, options) {
    return mapRunContext(runContext, {
        consent: createInteractiveConsentContext(options),
    });
}

/**
 * Authorize one concrete capability invocation. Approval is intentionally
 * scoped to the disclosed request: if a run contains a second tool call with
 * different resolved arguments, it must ask again.
 */
export async function requestRunConsent(
    runContext,
    request,
    { signal = null } = {},
) {
    if (signal?.aborted) {
        return refused(RUN_CONSENT_REFUSAL.CANCELLED, request);
    }

    const consent = runContext?.consent;
    if (consent?.userPresent !== true) {
        return refused(RUN_CONSENT_REFUSAL.NO_ACTIVE_USER, request);
    }

    const trigger = normalizeTrigger(consent.trigger || consent);
    if (!trigger) {
        return refused(RUN_CONSENT_REFUSAL.MISSING_TRIGGER, request);
    }

    const normalizedRequest = normalizeConsentRequest(request);
    if (!normalizedRequest) {
        return refused(RUN_CONSENT_REFUSAL.INVALID_REQUEST, request);
    }

    const requestConsent = brokerMethod(consent.broker);
    if (!requestConsent) {
        return refused(RUN_CONSENT_REFUSAL.NO_BROKER, normalizedRequest, trigger);
    }

    let brokerResult;
    let removeAbortListener = null;
    try {
        const brokerPromise = Promise.resolve(requestConsent({
            capability: normalizedRequest,
            trigger,
            ...(signal ? { signal } : {}),
        }));
        if (signal) {
            const aborted = new Promise((resolve) => {
                const onAbort = () => resolve(BROKER_ABORTED);
                signal.addEventListener("abort", onAbort, { once: true });
                removeAbortListener = () => {
                    signal.removeEventListener("abort", onAbort);
                };
            });
            brokerResult = await Promise.race([brokerPromise, aborted]);
        } else {
            brokerResult = await brokerPromise;
        }
    } catch (error) {
        return refused(
            RUN_CONSENT_REFUSAL.BROKER_FAILED,
            normalizedRequest,
            trigger,
            error,
        );
    } finally {
        removeAbortListener?.();
    }

    if (brokerResult === BROKER_ABORTED || signal?.aborted) {
        return refused(
            RUN_CONSENT_REFUSAL.CANCELLED,
            normalizedRequest,
            trigger,
        );
    }

    const approved = brokerResult === true || brokerResult?.approved === true;
    if (!approved) {
        const reason = REFUSAL_REASONS.has(brokerResult?.reason)
            ? brokerResult.reason
            : RUN_CONSENT_REFUSAL.DECLINED;
        return refused(
            reason,
            normalizedRequest,
            trigger,
        );
    }

    return {
        ok: true,
        approved: true,
        capability: normalizedRequest,
        trigger,
    };
}

/**
 * Adapter passed from the kernel runner to the parent-side Python runtime.
 * The parent asks for consent, then fetches and verifies only the wheel
 * dependency closure pinned in the checked manifest.
 */
export function createWheelConsentHandler(
    runContext,
    {
        signal = null,
        fetchImpl = globalThis.fetch,
        cryptoImpl = globalThis.crypto,
        manifest,
        origin,
    } = {},
) {
    return createPythonWheelBroker({
        authorize: (request) => requestRunConsent(runContext, {
            kind: "wheel",
            ...request,
        }, { signal }),
        signal,
        fetchImpl,
        cryptoImpl,
        ...(manifest ? { manifest } : {}),
        ...(origin ? { origin } : {}),
    });
}

export function normalizeConsentRequest(request) {
    if (!request || typeof request !== "object") return null;
    if (request.kind === "mcp_tool") {
        const server = nonEmpty(request.server);
        const tool = nonEmpty(request.tool);
        if (!server || !tool) return null;
        return {
            kind: "mcp_tool",
            server,
            tool,
            arguments: request.arguments ?? request.args ?? {},
        };
    }
    if (request.kind === "wheel") {
        const packageName = nonEmpty(
            request.packageName ?? request.package ?? request.name,
        );
        if (!packageName) return null;
        return {
            kind: "wheel",
            packageName,
            ...(nonEmpty(request.version) ? { version: nonEmpty(request.version) } : {}),
            ...(nonEmpty(request.reason) ? { reason: nonEmpty(request.reason) } : {}),
        };
    }
    return null;
}

function brokerMethod(broker) {
    if (typeof broker === "function") return broker;
    if (typeof broker?.requestConsent === "function") {
        return broker.requestConsent.bind(broker);
    }
    return null;
}

function normalizeTrigger(value) {
    if (!value || typeof value !== "object") return null;

    if (value.kind === "user" || value.kind === "model") {
        const text = nonEmpty(value.text);
        return text ? { kind: value.kind, text } : null;
    }

    const userText = nonEmpty(value.triggerText ?? value.userText);
    if (userText) return { kind: "user", text: userText };

    const modelReason = nonEmpty(value.modelReason);
    if (modelReason) return { kind: "model", text: modelReason };

    return null;
}

function refused(reason, request, trigger = null, cause = null) {
    const message = REFUSAL_COPY[reason] || REFUSAL_COPY[RUN_CONSENT_REFUSAL.DECLINED];
    return {
        ok: false,
        approved: false,
        outcome: "refused",
        refusal: {
            reason,
            message,
            ...(request ? { capability: normalizeConsentRequest(request) || request } : {}),
            ...(trigger ? { trigger } : {}),
        },
        error: new Error(message, cause ? { cause } : undefined),
    };
}

function nonEmpty(value) {
    if (value == null) return "";
    const text = String(value).trim();
    return text || "";
}
